import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import { api, clearCsrf, refreshCsrf, saveCsrf } from './api';
import type { Account } from './types';

interface AuthContextValue {
  account: Account | null;
  loading: boolean;
  login(email: string, password: string): Promise<void>;
  register(displayName: string, email: string, password: string): Promise<void>;
  logout(): Promise<void>;
  reload(): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [account, setAccount] = useState<Account | null>(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    try {
      const result = await api<{ account: Account }>('/api/account');
      setAccount(result.account);
      await refreshCsrf();
    } catch {
      clearCsrf();
      setAccount(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const value = useMemo<AuthContextValue>(() => ({
    account,
    loading,
    async login(email, password) {
      const result = await api<{ account: Account; csrfToken: string }>('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password, mode: 'web' }),
      });
      saveCsrf(result.csrfToken);
      setAccount(result.account);
    },
    async register(displayName, email, password) {
      const result = await api<{ account: Account; csrfToken: string }>('/api/auth/register', {
        method: 'POST',
        body: JSON.stringify({ displayName, email, password, mode: 'web' }),
      });
      saveCsrf(result.csrfToken);
      setAccount(result.account);
    },
    async logout() {
      await api('/api/auth/logout', { method: 'POST' });
      clearCsrf();
      setAccount(null);
    },
    reload,
  }), [account, loading, reload]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}
